// 🪟 별도 창 팝아웃 (2026-07-10) — 데탑(Tauri)은 WebviewWindow, 웹은 window.open.
// 대화와 일정을 나란히 보는 용도. 창 label 재사용(이미 열려 있으면 포커스만).
//
// 🔴 함정 — tauri.conf.json 의 main 창에 `additionalBrowserArgs` 를 지정하면 여기서 만드는 창이
//   전부 "떴다가 즉시 닫힘" 이 된다. Windows 는 그 값이 다른 WebView 를 같은 데이터 디렉토리에서
//   만들 수 없는데(@tauri-apps/api 타입 문서 명시), JS WebviewWindow 옵션엔 해당 필드가 없어
//   (2.11.0 기준) main 과 args 를 맞출 방법이 없다. 창별 브라우저 인자가 꼭 필요하면 Rust
//   WebviewWindowBuilder::additional_browser_args 로 창을 만들어야 한다. (0.2.295 회귀, 2026-08-07)
import { isTauri, isPopoutWindow } from './env';
import { isNativeApp } from '../utils/platform';
import { isNarrowLayout } from '../hooks/useIsMobile';
import { pushInternalPath } from '../utils/openExternal';

async function openPopout(label: string, path: string, title: string, size: { w: number; h: number }): Promise<void> {
	if (!isTauri()) {
		// 🔴 브라우저 팝업 차단이면 window.open 이 null 을 돌려주고 **아무 일도 안 일어난다**
		//   ("도움말을 눌러도 안 떠요"의 정체). 이때는 같은 창 이동으로 떨어뜨린다 (2026-09-12).
		const win = window.open(`/${path}`, label, `width=${size.w},height=${size.h},noopener=no`);
		if (!win) pushInternalPath(`/${path}`);
		return;
	}
	// 🦀 Rust 빌더 경로 (0.2.534, 2026-08-31 "별도창 안 뜸") — JS WebviewWindow 는 생성 실패가 이벤트로만 와서
	//   조용히 사라진다(0.2.532 dragDropEnabled 회귀 의심). Rust open_popout 은 실패를 동기 Result 로 돌려주고,
	//   팝아웃 안 드래그(HTML5 DnD·파일 드롭)를 위해 drag-drop 핸들러 비활성도 빌더에서 확정한다.
	try {
		const { invoke } = await import('@tauri-apps/api/core');
		await invoke('open_popout', { label, url: new URL(`/${path}`, window.location.origin).toString(), title, width: size.w, height: size.h });
		return;
	} catch (e) {
		console.warn(`[desktop] open_popout(rust) 실패 — JS 경로 폴백:`, e);
	}
	// 구버전 앱(open_popout 커맨드 없음) 폴백 — 종전 JS 생성 경로 (dragDropEnabled 옵션은 532 회귀 의심으로 제거)
	try {
		const { WebviewWindow } = await import('@tauri-apps/api/webviewWindow');
		const prev = await WebviewWindow.getByLabel(label);
		if (prev) { await prev.show(); await prev.unminimize(); await prev.setFocus(); return; }
		const win = new WebviewWindow(label, {
			url: path,
			title,
			width: size.w, height: size.h,
			minWidth: 720, minHeight: 480,
			resizable: true, center: true,
		});
		// 생성 실패는 예외가 아니라 이벤트로 오고 창만 조용히 사라진다 — 사유를 콘솔에 남겨 현장 진단 가능하게.
		void win.once('tauri://error', (e) => console.warn(`[desktop] ${label} window error:`, e.payload));
	} catch (e) {
		console.warn(`[desktop] ${label} popout failed:`, e);
		window.open(`/${path}`, label);
	}
}

export async function openCalendarWindow(): Promise<void> {
	return openPopout('tpt-calendar', 'calendar', 'TPTalk — 일정', { w: 1180, h: 780 });
}

// 🤖 AI 워크스페이스 별도 창 (2026-07-10) — 대화하면서 AI 분석을 나란히.
export async function openAiWindow(): Promise<void> {
	return openPopout('tpt-ai', 'ai', 'TPTalk — AI', { w: 1180, h: 780 });
}

// 🛠 관리자 별도 창 (2026-08-05 사용자 요청) — 관리 작업을 대화와 분리된 창에서.
export async function openAdminWindow(): Promise<void> {
	return openPopout('tpt-admin', 'admin', 'TPTalk — 관리자', { w: 1280, h: 860 });
}

// 📖 도움말 별도 창 (2026-08-16) — 대화를 보면서 사용법을 찾을 수 있게 분리된 창으로.
export async function openHelpWindow(): Promise<void> {
	return openPopout('tpt-help', 'help', 'TPTalk — 도움말', { w: 1240, h: 900 });
}

// 📮 VOC(문의·개선 요청) 별도 창 (2026-08-24 사용자 — "입력창이 작다") — 창 크기만큼 넓게 입력.
export async function openVocWindow(): Promise<void> {
	return openPopout('tpt-voc', 'voc', 'TPTalk — 문의·개선 요청', { w: 920, h: 780 });
}

// 🔗 링크 모음 별도 창 (2026-08-31 원격 "링크도 별도 팝업창으로") — 데탑·웹 기본. 모바일·APK 는 인앱 모달 유지.
export async function openLinksWindow(): Promise<void> {
	return openPopout('tpt-links', 'links', 'TPTalk — 링크', { w: 860, h: 720 });
}

// 📨 경조사 문자 발송 별도 창 (2026-08-27 사용자 — 프로필 메뉴 진입) — 대상 선택 트리가 넓어야 해서 큼직하게.
export async function openSmsWindow(): Promise<void> {
	return openPopout('tpt-sms', 'sms', 'TPTalk — 경조사 문자 발송', { w: 1180, h: 860 });
}

// 🧭 직접가입 인원 관리 별도 창 (2026-08-28 사용자 — 프로필 메뉴 진입) — 신청 목록·인원 표가 넓어야 해서 큼직하게.
export async function openSignupManageWindow(): Promise<void> {
	return openPopout('tpt-signup-manage', 'signup-manage', 'TPTalk — 직접가입 인원 관리', { w: 1180, h: 860 });
}
// 📋 설문조사 (2026-10-07) — 만들기·응답 화면이 길어 별도 창
export async function openFormsWindow(query = ''): Promise<void> {
	return openPopout('tpt-forms', query ? `forms?${query}` : 'forms', 'TPTalk — 설문조사', { w: 900, h: 860 });
}
// 🏢 조직도·인원 관리 (2026-10-07) — 인원 표가 넓어 별도 창
export async function openOrgManageWindow(): Promise<void> {
	return openPopout('tpt-org-manage', 'org-manage', 'TPTalk — 조직도·인원 관리', { w: 1180, h: 860 });
}

// 🔌 서비스 연동 별도 창 (2026-08-13 사용자 요청) — 연동 관리를 대화와 분리된 창에서.
//   방별 label 재사용(같은 방 연동 창 다시 열면 포커스만). 페이지는 /connect?rid=&name=.
export async function openConnectWindow(roomId: string, roomName?: string): Promise<void> {
	const safe = roomId.replace(/[^a-zA-Z0-9]/g, '').slice(0, 40) || 'x';
	const q = `connect?rid=${encodeURIComponent(roomId)}${roomName ? `&name=${encodeURIComponent(roomName)}` : ''}`;
	return openPopout(`tpt-connect-${safe}`, q, roomName ? `TPTalk — 연동 · ${roomName}` : 'TPTalk — 서비스 연동', { w: 1100, h: 780 });
}

// 🗂 잔디 이력 별도 창 (2026-08-29 사용자 — "우측 패널 말고 별도 팝업창, ⋯ 에서 진입").
//   과거 대화 수천~수만 건을 표로 훑는 화면이라 대화창 옆 좁은 패널로는 못 쓴다. 방별 label 재사용.
export async function openJandiWindow(roomId: string, roomName?: string): Promise<void> {
	const safe = roomId.replace(/[^a-zA-Z0-9]/g, '').slice(0, 40) || 'x';
	const q = `jandi?rid=${encodeURIComponent(roomId)}${roomName ? `&name=${encodeURIComponent(roomName)}` : ''}`;
	return openPopout(`tpt-jandi-${safe}`, q, roomName ? `TPTalk — 잔디 이력 · ${roomName}` : 'TPTalk — 잔디 이력', { w: 1180, h: 820 });
}

// 🗂 프로젝트 화면 별도 창 (2026-08-31 사용자 "프로젝트도 ㅁ 로 별도창") — win=1 로 팝아웃임을 표시(안에서는 버튼 숨김).
//   cid — 열리면서 그 카드 모달까지 (관리자 전사 대시보드의 지연 카드 행, 2026-09-06 2차-C). 딥링크 파서의 card 파라미터.
export async function openProjectsWindow(pid?: string, cid?: string): Promise<void> {
	const q = `projects?win=1${pid ? `&id=${encodeURIComponent(pid)}` : ''}${pid && cid ? `&card=${encodeURIComponent(cid)}` : ''}`;
	return openPopout('tpt-projects', q, 'TPTalk — 프로젝트', { w: 1380, h: 880 });
}

// 🗂 **프로젝트 진입 = 무조건 별도 창** (2026-09-09 사용자 "프로젝트는 누르면 무조건 새창") — 내비 아이콘·프로필 메뉴·홈 위젯·
//   방 공유 카드·캘린더 등 모든 진입점이 이 함수를 쓴다. query 는 'id=..&card=..' / 'view=mine&quick=late' 같은 딥링크 파라미터(ProjectsPage applyUrl).
//   멀티윈도우가 없는 네이티브 앱(안드·iOS)과 좁은 화면(모바일 웹)만 인앱 라우팅 폴백. 웹은 같은 label 창을 재사용(있으면 그 창이 이동).
const PJ_NAV_KEY = 'tpt-projects-pending-nav';
/** 프로젝트 창이 놓친 이동 경로(15초 이내) — 읽으면 지운다. 없으면 null. */
export function takePendingProjectsNav(): string | null {
	try {
		const raw = localStorage.getItem(PJ_NAV_KEY);
		if (!raw) return null;
		localStorage.removeItem(PJ_NAV_KEY);
		const p = JSON.parse(raw) as { path?: unknown; at?: unknown };
		return typeof p.path === 'string' && typeof p.at === 'number' && Date.now() - p.at < 15_000 ? p.path : null;
	} catch { return null; }
}

export function openProjectsAnywhere(query = ''): void {
	const q = query.replace(/^[?&]+/, '');
	// 🪟 좁은 **별도 창**(일정 창·줄인 방 창 등)에서 부르면 그 창 자신이 프로젝트로 갈아탔다 (2026-09-29 형 "별도창에서 바로가기 이동 안 됨",
	//   위젯 미니창 09-19 와 같은 함정) → 넓은 메인 창에 대상을 넘겨 거기서 연다. 프로젝트 창 자신(win=1)은 아래 종전 경로.
	if (!isNativeApp() && isNarrowLayout() && isPopoutWindow() && !window.location.pathname.startsWith('/projects')) {
		if (goMainNav('projects', { pjt: q })) return;
	}
	// 🔴 앱·좁은 화면은 원시 pushState — navigateInternal 로 보내면 그쪽 /projects 규약이 다시 이 함수를 불러 루프 (2026-09-11 회귀 수정)
	if (isNativeApp() || isNarrowLayout()) { pushInternalPath(`/projects${q ? `?${q}` : ''}`); return; }
	// 🪟 이미 프로젝트 창이 떠 있으면 그 창을 **그 항목으로 이동**시킨다 (2026-09-10 사용자 "프로젝트 창이 열려 있을 때 링크·바로가기를 눌러도 이동 안 됨").
	//   Rust open_popout 은 같은 label 이 있으면 포커스만 주고 URL 을 바꾸지 않는다 → Tauri 이벤트(emitTo)로 경로를 넘기고, 팝아웃 ProjectsPage 가 navigateInternal 로 재파싱.
	//   웹은 window.open(url, name) 이 표준대로 그 이름의 창을 새 URL 로 이동시키므로 종전 그대로.
	const path = `/projects?win=1${q ? `&${q}` : ''}`;
	void (async () => {
		if (isTauri()) {
			try {
				const { WebviewWindow } = await import('@tauri-apps/api/webviewWindow');
				const prev = await WebviewWindow.getByLabel('tpt-projects');
				if (prev) {
					// 🧷 그 창이 로딩·새로고침(versionGuard) 중이면 리스너가 아직 없어 이벤트가 유실된다 (2026-09-29 형 "이동 안 될 때가 많음") —
					//   보류분을 적어 두면 리스너가 붙는 순간 ProjectsPage 가 takePendingProjectsNav 로 복구한다(받으면 지운다, 15초 TTL).
					try { localStorage.setItem(PJ_NAV_KEY, JSON.stringify({ path, at: Date.now() })); } catch { /* private mode */ }
					const { emitTo } = await import('@tauri-apps/api/event');
					await emitTo('tpt-projects', 'tpt:popout-nav', { path });
					await openPopout('tpt-projects', path.slice(1), 'TPTalk — 프로젝트', { w: 1380, h: 880 });   // 포커스(Rust 는 기존 창이면 show/focus 만)
					return;
				}
			} catch (e) { console.warn('[desktop] projects popout nav 실패 — 새로 연다:', e); }
		}
		await openPopout('tpt-projects', path.slice(1), 'TPTalk — 프로젝트', { w: 1380, h: 880 });
	})();
}

// 🗂 프로젝트 설정 별도 창 (2026-08-29 사용자 #5) — 속성 편집을 본 화면과 분리(옆 모니터 가능). 프로젝트별 label 재사용.
export async function openPjtSettingsWindow(pid: string, name?: string): Promise<void> {
	const safe = pid.replace(/[^a-zA-Z0-9]/g, '').slice(0, 40) || 'x';
	return openPopout(`tpt-pjt-set-${safe}`, `pjt-settings?id=${encodeURIComponent(pid)}`,
		name ? `TPTalk — 프로젝트 설정 · ${name}` : 'TPTalk — 프로젝트 설정', { w: 640, h: 760 });
}

// ⚖ PJT 카드 2장 비교 별도 창 (2026-08-31 사용자 "금주/전주 비교") — 데이터는 서버 재조회(URL 에 pid+cid 2개만).
export async function openPjtCompareWindow(pid: string, a: string, b: string): Promise<void> {
	const safe = pid.replace(/[^a-zA-Z0-9]/g, '').slice(0, 40) || 'x';
	return openPopout(`tpt-pjt-cmp-${safe}`, `pjt-compare?pid=${encodeURIComponent(pid)}&a=${encodeURIComponent(a)}&b=${encodeURIComponent(b)}`,
		'TPTalk — 카드 비교', { w: 1280, h: 860 });
}

// 🖼 파일/사진 미리보기 별도 창 (2026-08-18 사용자) — 사진을 보면서 대화할 수 있게 분리.
//   파일 목록은 previewHandoff(localStorage)로 넘기고 URL 엔 키만 싣는다. 키마다 새 창(여러 장 동시 보기).
export async function openPreviewWindow(key: string, title?: string): Promise<void> {
	const safe = key.replace(/[^a-zA-Z0-9]/g, '').slice(0, 40) || 'x';
	return openPopout(`tpt-preview-${safe}`, `preview?k=${encodeURIComponent(key)}`,
		title ? `TPTalk — ${title}` : 'TPTalk — 미리보기', { w: 1080, h: 800 });
}

// 🖥 화면 공유 시청 별도 창 (2026-08-18 사용자) — 대화창을 가리지 않고 발표 화면만 따로 본다.
//   창 안에서 독립적으로 참여(join)한다 — 시그널링이 서버 기반이라 메인 창과 상태를 공유할 필요가 없다.
export async function openShareViewerWindow(shareId: string, hostName?: string): Promise<void> {
	const safe = shareId.replace(/[^a-zA-Z0-9]/g, '').slice(0, 40) || 'x';
	const q = `share?sid=${encodeURIComponent(shareId)}${hostName ? `&host=${encodeURIComponent(hostName)}` : ''}`;
	return openPopout(`tpt-share-${safe}`, q, hostName ? `TPTalk — ${hostName}` : 'TPTalk — 화면 공유', { w: 1100, h: 720 });
}

// 💬 대화방 별도 창 (2026-07-15) — 방마다 독립 창으로 띄워 여러 대화를 동시에. main 창은 그대로.
//   label 을 roomId 로 구분해 방별 창 재사용(같은 방 다시 열면 포커스만). 창은 solo 모드 ChatPage(/room?id=).
//   jumpMessageId — 열리면서 특정 메시지로 점프 (AI 인용 '원문으로 이동', 2026-08-05).
export async function openRoomWindow(roomId: string, title?: string, jumpMessageId?: string): Promise<void> {
	const safe = roomId.replace(/[^a-zA-Z0-9]/g, '').slice(0, 40) || 'x';
	const msgQ = jumpMessageId ? `&msg=${encodeURIComponent(jumpMessageId)}` : '';
	return openPopout(`tpt-room-${safe}`, `room?id=${encodeURIComponent(roomId)}${msgQ}`, title ? `TPTalk — ${title}` : 'TPTalk', { w: 960, h: 800 });
}

// 🧭 팝아웃 창 → **메인 창으로 이동**시키기 (2026-09-14 사용자 "프로젝트 새창에서 채팅창이나 알림 등의 메뉴로 이동이 안됨")
//   프로젝트는 전용 창 규약(위 openProjectsAnywhere)이라, 그 창의 상단 네비에서 '대화·알림·홈…'을 누르면
//   **그 창을 채팅으로 갈아타는 게 아니라** 이미 떠 있는 메인 창을 그 화면으로 보내고 포커스를 준다.
//   종전에는 팝아웃 안에서 nav('/chat') 을 불러 프로젝트 창이 채팅 창으로 바뀌었고(메인 창은 그대로),
//   메인 창이 이미 채팅이라 사용자 눈에는 "아무 데도 안 간다"로 보였다.
//   받는 쪽 = App.tsx 의 메인 창 리스너(Tauri 이벤트 / 웹 postMessage) → pushInternalPath('/chat') + tpt:switch-nav.
export const MAIN_NAV_EVENT = 'tpt:main-nav';

/** 팝아웃에서 호출 — 메인 창을 navId 화면으로 보내고 포커스. 보낼 곳이 없으면 false(호출부가 종전대로 처리). */
/** 메인 창으로 보낼 이동 대상 (2026-09-19 위젯 미니창) — 없으면 종전대로 화면 전환만 한다.
 *  🔴 위젯 미니창(340px)은 isNarrowLayout()=true 라, 거기서 openProjectsAnywhere 를 직접 부르면
 *  전용 창이 아니라 **미니창 자신이** 프로젝트 화면으로 갈아탄다(09-19 실사고와 같은 함정).
 *  그래서 대상만 실어 보내고 실제 열기는 넓은 메인 창이 한다. */
export type MainNavTarget = {
	/** 이 방을 연다 (tpt:open-room) */
	roomId?: string;
	/** 방을 열고 이 메시지로 점프 (tpt:jump-message) — 팝아웃 안의 /chat?rid=&mid= 링크 (2026-09-29) */
	messageId?: string;
	/** 프로젝트 전용 창을 연다 — openProjectsAnywhere 의 query ('id=…&card=…' · 'view=mine' · '') */
	pjt?: string;
	/** 일정 전용 창을 연다 */
	cal?: boolean;
};

export { isPopoutWindow };

export function goMainNav(navId: string, target?: MainNavTarget): boolean {
	if (isTauri()) {
		void (async () => {
			try {
				const { emitTo } = await import('@tauri-apps/api/event');
				await emitTo('main', MAIN_NAV_EVENT, { id: navId, ...(target || {}) });
				// 🔴 프로젝트·일정은 메인 창이 **전용 창**을 열어 그쪽에 포커스를 준다 — 여기서 메인 창까지 앞으로 올리면
				//   두 포커스가 경주해 늦게 끝난 메인 창이 이겨 프로젝트·일정 창이 뒤로 숨었다 (2026-09-29 형 "바로가기 이동 안 될 때가 많음").
				if (target?.pjt !== undefined || target?.cal) return;
				const { WebviewWindow } = await import('@tauri-apps/api/webviewWindow');
				const main = await WebviewWindow.getByLabel('main');
				if (main) { await main.show(); await main.unminimize(); await main.setFocus(); }
			} catch (e) { console.warn('[desktop] goMainNav 실패:', e); }
		})();
		return true;
	}
	// 웹 — window.open 으로 뜬 창이라 opener 가 메인 탭. 닫혔으면 false 로 떨어뜨린다.
	try {
		const op = window.opener as Window | null;
		if (op && !op.closed) {
			op.postMessage({ type: MAIN_NAV_EVENT, id: navId, ...(target || {}) }, window.location.origin);
			try { op.focus(); } catch { /* 브라우저가 막아도 이동은 된다 */ }
			return true;
		}
	} catch { /* 크로스 오리진 등 */ }
	return false;
}
